import React, { useState } from 'react';
import { X, Mail, Lock, LogIn, UserPlus, AlertCircle } from 'lucide-react';

const AuthModal = ({ isOpen, onClose, onGoogleLogin, onEmailLogin, onEmailRegister, onPasswordReset }) => {
    const [mode, setMode] = useState('login'); // 'login', 'register', 'reset'
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [resetSent, setResetSent] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            if (mode === 'login') {
                await onEmailLogin(email, password);
                onClose();
            } else if (mode === 'register') {
                await onEmailRegister(email, password);
                onClose();
            } else if (mode === 'reset') {
                await onPasswordReset(email);
                setResetSent(true);
            }
        } catch (err) {
            console.error(err);
            if (err.code === 'auth/invalid-credential') setError("帳號或密碼錯誤");
            else if (err.code === 'auth/email-already-in-use') setError("此 Email 已被註冊");
            else if (err.code === 'auth/weak-password') setError("密碼強度不足 (至少 6 字元)");
            else if (err.code === 'auth/user-not-found') setError("找不到此帳號");
            else setError(err.message || "發生錯誤，請稍後再試");
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleClick = async () => {
        setError('');
        setLoading(true);
        try {
            await onGoogleLogin();
            onClose();
        } catch (err) {
            console.error(err);
            setError("Google 登入失敗");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50">
                    <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                        {mode === 'login' ? <LogIn className="w-5 h-5 text-indigo-500" /> :
                            mode === 'register' ? <UserPlus className="w-5 h-5 text-emerald-500" /> :
                                <Lock className="w-5 h-5 text-orange-500" />}
                        {mode === 'login' ? '登入帳號' : mode === 'register' ? '註冊新帳號' : '重設密碼'}
                    </h2>
                    <button onClick={onClose} className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500 dark:text-slate-400">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-6">
                    {mode === 'reset' && resetSent ? (
                        <div className="text-center space-y-4">
                            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto">
                                <Mail className="w-8 h-8" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">重設信已發送</h3>
                            <p className="text-slate-500 dark:text-slate-400">請檢查您的信箱 {email}，並依照指示重設密碼。</p>
                            <button onClick={() => { setMode('login'); setResetSent(false); }} className="text-indigo-600 font-bold hover:underline">返回登入</button>
                        </div>
                    ) : (
                        <>
                            {mode !== 'reset' && (
                                <>
                                    {/* Google Login */}
                                    <button
                                        onClick={handleGoogleClick}
                                        disabled={loading}
                                        className="w-full py-2.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all shadow-sm"
                                    >
                                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                        </svg>
                                        使用 Google 帳號{mode === 'login' ? '登入' : '註冊'}
                                    </button>

                                    <div className="relative flex items-center justify-center">
                                        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200 dark:border-slate-700"></div></div>
                                        <span className="relative bg-white dark:bg-slate-900 px-4 text-xs text-slate-400 dark:text-slate-500 uppercase font-bold tracking-wider">OR</span>
                                    </div>
                                </>
                            )}

                            {/* Email Form */}
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {error && (
                                    <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded-lg flex items-center gap-2">
                                        <AlertCircle className="w-4 h-4 shrink-0" />
                                        {error}
                                    </div>
                                )}
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 ml-1">Email</label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <input
                                            type="email"
                                            required
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none text-slate-800 dark:text-slate-100 transition-all"
                                            placeholder="name@example.com"
                                        />
                                    </div>
                                </div>
                                {mode !== 'reset' && (
                                    <div className="space-y-1">
                                        <div className="flex justify-between">
                                            <label className="text-xs font-bold text-slate-500 dark:text-slate-400 ml-1">密碼</label>
                                            {mode === 'login' && (
                                                <button type="button" onClick={() => { setMode('reset'); setError(''); }} className="text-xs text-indigo-500 hover:text-indigo-600 font-bold">
                                                    忘記密碼？
                                                </button>
                                            )}
                                        </div>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                            <input
                                                type="password"
                                                required
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none text-slate-800 dark:text-slate-100 transition-all"
                                                placeholder="••••••••"
                                                minLength={6}
                                            />
                                        </div>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`w-full py-3 rounded-xl font-bold text-white shadow-lg shadow-indigo-200 dark:shadow-indigo-900/30 flex items-center justify-center gap-2 transition-all ${loading ? 'bg-slate-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 hover:scale-[1.02]'}`}
                                >
                                    {loading ? '處理中...' : (mode === 'login' ? '登入' : mode === 'register' ? '註冊' : '發送重設信')}
                                </button>
                            </form>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800 text-center">
                    {mode === 'reset' ? (
                        <button
                            onClick={() => { setMode('login'); setError(''); }}
                            className="text-sm text-slate-500 dark:text-slate-400 hover:text-indigo-600 font-bold"
                        >
                            返回登入
                        </button>
                    ) : (
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            {mode === 'login' ? '還沒有帳號？' : '已經有帳號了？'}
                            <button
                                onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
                                className="ml-1 text-indigo-600 dark:text-indigo-400 font-bold hover:underline"
                            >
                                {mode === 'login' ? '立即註冊' : '登入'}
                            </button>
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AuthModal;
